import { Table } from '@/components';
import {
  FilterSelect,
  QueryStates,
  RunnerPage,
  uniqueOptions,
} from '@/features/runner-monitor/RunnerPage';
import { Labels } from '@/features/runner-monitor/RunnersTable';
import {
  MockBadge,
  QueueReasonBadge,
  RunnerStatusBadge,
} from '@/features/runner-monitor/StatusBadge';
import { dateTime, duration, secondsBetween, useNow } from '@/features/runner-monitor/format';
import { useRunnerQueue } from '@/features/runner-monitor/hooks';
import { useSearchParams } from '@solidjs/router';
import type { QueuedJob } from '@uchat-team-management/shared';
import { For, Show } from 'solid-js';

const QueueTable = (props: { jobs: QueuedJob[] }) => {
  const now = useNow(1000);
  return (
    <Table
      data={props.jobs}
      rowKey={(j) => j.id}
      columns={[
        {
          key: 'job',
          header: 'Workflow / job',
          render: (j) => (
            <div class="min-w-0">
              <div class="flex items-center gap-2 font-medium">
                <a
                  href={j.htmlUrl}
                  target="_blank"
                  rel="noreferrer"
                  class="text-brand-600 hover:underline dark:text-brand-400"
                >
                  {j.jobName}
                </a>
                <MockBadge source={j.source} />
              </div>
              <div class="text-xs text-neutral-500">
                {j.workflowName} · {j.repo}
                {j.headBranch ? ` · ${j.headBranch}` : ''}
              </div>
            </div>
          ),
        },
        { key: 'labels', header: 'Needs labels', render: (j) => <Labels labels={j.labels} /> },
        {
          key: 'queued',
          header: 'Queued · waiting',
          render: (j) => (
            <div class="text-xs tabular-nums">
              <div>{dateTime(j.queuedAt)}</div>
              <div class="font-medium">{duration(secondsBetween(j.queuedAt, null, now()))}</div>
            </div>
          ),
        },
        {
          key: 'reason',
          header: 'Waiting for',
          render: (j) => <QueueReasonBadge reason={j.queueReason} />,
        },
        {
          key: 'eligible',
          header: 'Runners with matching labels',
          render: (j) => (
            <Show
              when={j.eligibleRunners.length > 0}
              fallback={<span class="text-xs text-red-600">None — no runner can take it</span>}
            >
              <ul class="space-y-1 text-xs">
                <For each={j.eligibleRunners}>
                  {(r) => (
                    <li class="flex items-center gap-2">
                      <span>{r.name}</span>
                      <RunnerStatusBadge status={r.status} />
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          ),
        },
      ]}
    />
  );
};

export default function RunnersQueue() {
  const q = useRunnerQueue();
  const [params, setParams] = useSearchParams<{ repo?: string }>();
  const jobs = () => (q.data?.data ?? []).filter((j) => !params.repo || j.repo === params.repo);
  const forRunner = () => jobs().filter((j) => (j.queueReason ?? 'runner') === 'runner');
  const blocked = () => jobs().filter((j) => (j.queueReason ?? 'runner') !== 'runner');

  return (
    <RunnerPage title="Job queue" sub="Jobs GitHub has not started yet" updatedAt={q.dataUpdatedAt}>
      <div class="flex flex-wrap gap-3">
        <FilterSelect
          label="Repo"
          value={params.repo}
          options={uniqueOptions((q.data?.data ?? []).map((j) => j.repo))}
          onChange={(repo) => setParams({ repo })}
        />
      </div>
      <p class="text-xs text-neutral-500">
        GitHub assigns a queued job to a runner only when it starts — the runners listed are the
        ones allowed to take it (same repo, labels match), not a prediction of which will.
      </p>
      <QueryStates
        pending={q.isPending}
        error={q.error}
        empty={jobs().length === 0}
        emptyTitle="Nothing queued"
        emptyBody="Every self-hosted job has a runner."
      >
        <Show when={forRunner().length > 0}>
          <h2 class="text-sm font-semibold">Waiting for a runner ({forRunner().length})</h2>
          <QueueTable jobs={forRunner()} />
        </Show>
        <Show when={blocked().length > 0}>
          <h2 class="text-sm font-semibold">
            Waiting on approval, needed jobs or concurrency ({blocked().length})
          </h2>
          <QueueTable jobs={blocked()} />
        </Show>
      </QueryStates>
    </RunnerPage>
  );
}
